<template>
<div class="page">
  <div>
    <div class="d-flex flex-nowrap align-center py-1 mb-3" :style="'gap:60px'">
      <div class="d-flex flex-nowrap" :style="'gap:4px'">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div v-on="on" class="d-inline-block">
              <v-btn
                color="primary"
                class=" icon px-0"
                outlined
                v-bind="attrs"
                :disabled="!canExport"
                @click="exportToExcel"
              >
                <v-icon :color="canExport ? 'primary' : '#9BA1B5'">mdi-upload</v-icon>
              </v-btn>
            </div>
          </template>
          <span>Выгрузить в Excel</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div v-on="on" class="d-inline-block">
              <v-btn v-if="$store.getters['auth/isAllowed']('view_testresultscompliencereport')"
                     color="primary" class="px-0 icon" outlined v-bind="attrs" :disabled="isGraphDefault" @click="resetGraph">
                <v-icon :color="isGraphDefault ? '#9BA1B5' : 'primary'">mdi-filter-remove-outline</v-icon>
              </v-btn>
            </div>
          </template>
          <span>Сбросить все фильтры</span>
        </v-tooltip>
      </div>
      <div>
        <div class="d-inline-block">
          <v-menu
            v-model="datePickerShow"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            @input="onDatePickerToggle"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="dateRangeText || null"
                label="Выбрать дату"
                append-icon="mdi-calendar"
                :prepend-inner-icon="dateRangeText ? 'mdi-close' : null"
                @click:prepend-inner="clearDates"
                class="font-sm"
                dense readonly outlined hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>

            <!-- Диапазон собирается локально и попадает в адрес только по «OK». -->
            <v-date-picker
              v-model="pickerDates"
              no-title
              locale="ru-RU"
              range
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="cancelDates"> Отменить</v-btn>
              <v-btn text color="primary" :disabled="pickerDates.length !== 2" @click="applyDates"> OK</v-btn>
            </v-date-picker>
          </v-menu>
        </div>
      </div>
      <!--
        Поиск идёт отдельным запросом без пагинации, а не через общий фильтр таблицы менеджеров:
        иначе поиск на графике менял бы фильтры таблицы и её Excel-выгрузки.
      -->
      <v-autocomplete
        :value="currentManager"
        :items="managerItems"
        :item-text="managerName"
        item-value="id"
        return-object
        :loading="managersLoading"
        label="ФИО менеджера"
        append-icon="mdi-magnify"
        no-data-text="Начните вводить ФИО"
        outlined
        dense
        hide-details
        clearable
        no-filter
        class="managers-graph__manager"
        @change="onManagerSelect"
        @update:search-input="onManagerSearch"
      />
<!--      <custom-select-->
<!--        :dense="true"-->
<!--        :label="'Статус тестирования'"-->
<!--        :outline="true"-->
<!--        :items="statuses"-->
<!--        :offset-y="true"-->
<!--        :model-value="currentStatus"-->
<!--        @update:model="setStatusTesting"-->
<!--      />-->
      <v-btn @click="createGraph">
        Построить
      </v-btn>
    </div>

  </div>
  <div class="col-12 d-flex align-center pt-6 pb-3">
    <h1 class="font-lg">История расходов тестов</h1>
  </div>
  <BarChart :data="chartData" v-show="chartData.datasets.length"/>
  <div v-show="!chartData.datasets.length">
    <div
      :class="' d-flex align-center bg-whiter  justify-space-between '"
    >
      <div>
        <v-img
          src="@/assets/images/adminPanel/empty_star.webp"
          max-width="136px"
          :class="'BannerPreview__img'"

        />
      </div>
      <div :class="'BannerPreview__title'">Данные по расходам теста остутствуют</div>
      <div>
        <v-img
          src="@/assets/images/adminPanel/empty_star.webp"
          max-width="136px"
          :class="'BannerPreview__img'"
        />
      </div>
    </div>
  </div>

</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BarChart from '@/components/ui/BarChart.vue';
import ManagerStatsToTable from '@/common/exportExcel/ManagerStatsToTable';

const pad = (value) => String(value).padStart(2, '0');

// YYYY-MM-DD по локальному времени (toISOString дал бы дату по UTC).
const toDateString = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const lastMonthPeriod = () => {
  const end = new Date();
  const start = new Date();
  start.setMonth(end.getMonth() - 1);
  return [toDateString(start), toDateString(end)];
};

export default {
  name: 'ManagersGraph',
  components: {
    BarChart,
  },
  props: {
    chartData: {
      type: Object,
      default: () => ({ datasets: [] }),
    },
  },
  data: () => ({
    datePickerShow: false,
    pickerDates: [],
    currentManager: null,
    managerSearchTimer: null,
    // Параметры последнего построения — чтобы Excel описывал именно то, что на графике,
    // даже если фильтры поменяли, но «Построить» ещё не нажали.
    lastBuild: null,
  }),
  computed: {
    ...mapGetters({
      managerStats: 'users/managers/statsManager',
      loadedManager: 'users/managers/manager',
      managerOptions: 'users/managers/bookingSearchResults',
      managersLoading: 'users/managers/bookingSearchLoading',
    }),
    appliedDates() {
      const { dateStart, dateEnd } = this.$route.query;
      return dateStart && dateEnd ? [dateStart, dateEnd] : [];
    },
    dateRangeText() {
      if (!this.appliedDates.length) return '';
      return `${this.formatDate(this.appliedDates[0])} - ${this.formatDate(this.appliedDates[1])}`;
    },
    managerItems() {
      const items = [...(this.managerOptions || [])];
      if (this.currentManager && !items.some((item) => item.id === this.currentManager.id)) {
        items.unshift(this.currentManager);
      }
      return items;
    },
    hasChartData() {
      return Boolean(this.chartData.datasets && this.chartData.datasets.length);
    },
    canExport() {
      return this.hasChartData && Boolean(this.lastBuild);
    },
    isGraphDefault() {
      const [start, end] = lastMonthPeriod();
      const isDefaultPeriod = this.appliedDates[0] === start && this.appliedDates[1] === end;
      return !this.currentManager && isDefaultPeriod && !this.hasChartData;
    },
  },
  methods: {
    ...mapActions({
      loadManager: 'users/managers/loadManager',
      searchManagers: 'users/managers/searchManagersForBooking',
      setStatsManger: 'users/managers/setStatsManger',
      resetStatsManager: 'users/managers/resetStatsManager',
    }),
    setRouteQuery(patch) {
      return this.$router.push({ query: { ...this.$route.query, ...patch } }).catch(() => {});
    },
    formatDate(dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    },
    managerName(item) {
      if (!item) return '';
      return `${item.last_name || ''} ${item.first_name || ''}`.trim() || item.email || `#${item.id}`;
    },

    onDatePickerToggle(isOpen) {
      if (isOpen) this.pickerDates = [...this.appliedDates];
    },
    cancelDates() {
      this.datePickerShow = false;
    },
    applyDates() {
      // даты могут быть выбраны в любом порядке
      const [dateStart, dateEnd] = [...this.pickerDates].sort();
      this.setRouteQuery({ dateStart, dateEnd });
      this.datePickerShow = false;
    },
    clearDates() {
      this.setRouteQuery({ dateStart: undefined, dateEnd: undefined });
    },

    onManagerSelect(manager) {
      this.currentManager = manager || null;
      this.setRouteQuery({ managerId: manager ? String(manager.id) : undefined });
    },
    onManagerSearch(value) {
      clearTimeout(this.managerSearchTimer);
      // после выбора в поле подставляется имя менеджера — это не новый поиск
      if (!value || (this.currentManager && value === this.managerName(this.currentManager))) return;
      this.managerSearchTimer = setTimeout(() => {
        this.searchManagers(value.trim());
      }, 400);
    },

    dayBoundaryISO(dateString, isEnd) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = isEnd
        ? new Date(year, month - 1, day, 23, 59, 59, 999)
        : new Date(year, month - 1, day, 0, 0, 0, 0);
      return date.toISOString();
    },
    async createGraph() {
      const data = {};
      if (this.currentManager) {
        data.manager_id = this.currentManager.id;
      }
      if (this.appliedDates.length) {
        data.start_date = this.dayBoundaryISO(this.appliedDates[0], false);
        data.end_date = this.dayBoundaryISO(this.appliedDates[1], true);
      }
      await this.setStatsManger(data);
      this.lastBuild = {
        managerName: this.currentManager ? this.managerName(this.currentManager) : 'Все менеджеры',
        period: this.dateRangeText || 'За всё время',
      };
    },
    resetGraph() {
      const [dateStart, dateEnd] = lastMonthPeriod();
      this.currentManager = null;
      this.lastBuild = null;
      this.setRouteQuery({ managerId: undefined, dateStart, dateEnd });
      this.resetStatsManager();
    },
    exportToExcel() {
      if (!this.canExport) return;
      new ManagerStatsToTable({
        tests: this.managerStats.tests,
        totalSent: this.managerStats.total_sent,
        managerName: this.lastBuild.managerName,
        period: this.lastBuild.period,
      }).toTable();
    },
  },

  mounted() {
    // Статистика в сторе могла остаться с прошлого визита — без параметров построения
    // её нельзя ни подписать, ни выгрузить, поэтому начинаем с пустого графика.
    this.resetStatsManager();

    if (!this.appliedDates.length) {
      const [dateStart, dateEnd] = lastMonthPeriod();
      this.$router.replace({ query: { ...this.$route.query, dateStart, dateEnd } }).catch(() => {});
    }

    const { managerId } = this.$route.query;
    if (managerId) {
      this.loadManager(managerId)
        .then(() => {
          this.currentManager = this.loadedManager || null;
        })
        .catch(() => {
          this.setRouteQuery({ managerId: undefined });
        });
    }
  },
  beforeDestroy() {
    clearTimeout(this.managerSearchTimer);
  },
};
</script>

<style>
.managers-graph__manager {
  min-width: 260px;
}

/* Vuetify разворачивает иконку справа при открытом меню — для стрелки, но не для лупы. */
.managers-graph__manager.v-select--is-menu-active .v-input__icon--append .v-icon {
  transform: none;
}
</style>
